import { createFileRoute } from '@tanstack/react-router';

import { useBesluitId } from '@/api/queries/besluit';
import { Button } from '@/components/Button/Button';
import { Warning } from '@/components/Warning';
import { GRONDSLAGEN, Grondslag } from '@/constants/grondslagen';
import { useAddGrondslagHoofdstuk } from '@/modals/AddGrondslagHoofdstuk';
import { useUserStore } from '@/stores/user';

import { ToolbarContentPage } from './-components/ToolbarContentPage/ToolbarContentPage';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/voorschriften/hoofdstuk'
)({
  component: VoorschriftenComponent,
  loader: async ({ context: { queryClient }, params: { besluitId } }) => {
    await queryClient.ensureQueryData(useBesluitId.getOptions(besluitId));
  },
});

/**
 * Route component
 */
function VoorschriftenComponent() {
  const { besluitId } = Route.useParams();
  const besluit = useBesluitId(besluitId);

  if (!besluit.data) return null;

  return (
    <>
      <div className="container-default relative mx-auto min-h-screen px-4 pt-8">
        <h2 className="style-h2-bold mb-1 text-black">
          Vergunningdeel toevoegen
        </h2>

        <p className="style-h5 mb-2">
          Het besluit is helemaal leeg, voeg eenvoudig een vergunningdeel toe.
        </p>

        <ul className="grid grid-cols-5 gap-2">
          {GRONDSLAGEN.map((grondslag) => (
            <GrondslagButton
              key={`voorschriften-hoofdstuk-${grondslag.id}`}
              grondslag={grondslag}
              disabled={besluit.data.Status === 'Bevroren'}
            />
          ))}
        </ul>
      </div>

      <ToolbarContentPage besluit={besluit.data} />
    </>
  );
}

const GrondslagButton = ({
  grondslag,
  disabled,
}: {
  grondslag: Grondslag;
  disabled?: boolean;
}) => {
  const { besluitId, bedrijfLineageId } = Route.useParams();
  const userId = useUserStore((state) => state.user?.ID);

  const addGrondslagHoofdstuk = useAddGrondslagHoofdstuk();

  return (
    <li>
      <Button
        variant="secondary"
        className="w-full"
        size="xl"
        disabled={disabled}
        icon={grondslag.icon}
        state={addGrondslagHoofdstuk.isPending ? 'loading' : undefined}
        onClick={() => {
          if (!userId) return;

          addGrondslagHoofdstuk.mutate({
            userId,
            bedrijfLineageId,
            besluitId,
            grondslag,
          });
        }}
      >
        {grondslag.label}
      </Button>

      {addGrondslagHoofdstuk.error?.message && (
        <Warning className="my-1" variant="error" size="small">
          <Warning.Description>
            {addGrondslagHoofdstuk.error.message}
          </Warning.Description>
        </Warning>
      )}
    </li>
  );
};
